import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Trash2, ExternalLink, Pause, Play,
  Shirt, Sofa, Gem, UtensilsCrossed, Ticket, Plane,
  Bell, Mail, Zap
} from 'lucide-react';

const CATEGORY_ICONS = {
  clothing: Shirt,
  furniture: Sofa,
  jewelry: Gem,
  food: UtensilsCrossed,
  experience: Ticket,
  travel: Plane,
};

const CATEGORY_COLORS = {
  clothing: '#F472B6',
  furniture: '#0D9488',
  jewelry: '#FACC15',
  food: '#FB923C',
  experience: '#A855F7',
  travel: '#60A5FA',
};

const TrackerDetail = () => {
  const { trackerId } = useParams();
  const navigate = useNavigate();
  const [tracker, setTracker] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrackerData();
  }, [trackerId]);

  const fetchTrackerData = async () => {
    try {
      const [trackerRes, dealsRes] = await Promise.all([
        api.get(`/trackers/${trackerId}`),
        api.get('/deals?limit=50'),
      ]);
      setTracker(trackerRes.data);
      // Filter deals for this tracker
      const trackerDeals = dealsRes.data.filter(d => d.tracker_id === trackerId);
      setDeals(trackerDeals);
    } catch (error) {
      toast.error('Failed to load tracker');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    try {
      await api.patch(`/trackers/${trackerId}`, { is_active: !tracker.is_active });
      setTracker({ ...tracker, is_active: !tracker.is_active });
      toast.success(tracker.is_active ? 'Tracker paused' : 'Tracker resumed');
    } catch (error) {
      toast.error('Failed to update tracker');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this tracker?')) return;
    
    try {
      await api.delete(`/trackers/${trackerId}`);
      toast.success('Tracker deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete tracker');
    }
  };

  const handleNotificationToggle = async (type, value) => {
    try {
      const update = type === 'email' 
        ? { notify_email: value } 
        : { notify_inapp: value };
      await api.patch(`/trackers/${trackerId}`, update);
      setTracker({ ...tracker, ...update });
      toast.success('Preferences updated');
    } catch (error) {
      toast.error('Failed to update preferences');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tracker) return null;

  const Icon = CATEGORY_ICONS[tracker.category] || Shirt;
  const color = CATEGORY_COLORS[tracker.category] || '#F472B6';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Dashboard
            </button>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline"
                size="icon"
                onClick={handleToggle}
                data-testid="toggle-tracker-btn"
              >
                {tracker.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </Button>
              <Button 
                variant="outline"
                size="icon"
                onClick={handleDelete}
                className="text-destructive hover:text-destructive"
                data-testid="delete-tracker-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tracker Info */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-3xl p-8 mb-8"
        >
          <div className="flex items-start gap-6">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${color}20` }}
            >
              <Icon className="w-8 h-8" style={{ color }} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="font-heading text-2xl font-bold">{tracker.name}</h1>
                {!tracker.is_active && (
                  <span className="px-2 py-1 rounded-full bg-muted text-muted-foreground text-xs">
                    Paused
                  </span>
                )}
              </div>
              <p className="text-muted-foreground capitalize mb-4">
                {tracker.category}
                {tracker.subcategory && ` • ${tracker.subcategory}`}
              </p>
              <div className="flex flex-wrap gap-3">
                <span 
                  className="px-4 py-2 rounded-full text-sm font-medium"
                  style={{ backgroundColor: `${color}20`, color }}
                >
                  {tracker.discount_threshold}%+ off
                </span>
                <span className="px-4 py-2 rounded-full bg-muted text-sm">
                  {tracker.brands.length > 0 ? `${tracker.brands.length} brands` : 'All brands'}
                </span>
                <span className="px-4 py-2 rounded-full bg-success/20 text-success text-sm">
                  {tracker.deals_found} deals found
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Notification Settings */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-2xl p-6 mb-8"
        >
          <h2 className="font-heading text-lg font-semibold mb-4">Notifications</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">In-App Notifications</p>
                  <p className="text-sm text-muted-foreground">Get notified in the app</p>
                </div>
              </div>
              <Switch 
                checked={tracker.notify_inapp}
                onCheckedChange={(value) => handleNotificationToggle('inapp', value)}
                data-testid="notify-inapp-switch"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Email Notifications</p>
                  <p className="text-sm text-muted-foreground">Receive deal alerts via email</p>
                </div>
              </div>
              <Switch 
                checked={tracker.notify_email}
                onCheckedChange={(value) => handleNotificationToggle('email', value)}
                data-testid="notify-email-switch"
              />
            </div>
          </div>
        </motion.div>

        {/* Selected Brands */}
        {tracker.brands.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass rounded-2xl p-6 mb-8"
          >
            <h2 className="font-heading text-lg font-semibold mb-4">Tracked Brands</h2>
            <div className="flex flex-wrap gap-2">
              {tracker.brands.map((brand) => (
                <span key={brand} className="px-3 py-1.5 rounded-full bg-muted text-sm">
                  {brand}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        {/* Deals */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="font-heading text-lg font-semibold mb-4">Deals Found</h2>
          
          {deals.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-2">No deals yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                We're watching for deals that match your criteria. You'll be notified as soon as we find something!
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {deals.map((deal, index) => (
                <motion.div
                  key={deal.deal_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="glass rounded-2xl p-4 flex gap-4 group hover:border-primary/30 transition-all cursor-pointer"
                  onClick={() => window.open(deal.url, '_blank')}
                  data-testid={`deal-${deal.deal_id}`}
                >
                  {deal.image_url && (
                    <div className="w-20 h-20 rounded-xl bg-card overflow-hidden flex-shrink-0">
                      <img 
                        src={deal.image_url} 
                        alt={deal.product_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-semibold truncate">{deal.product_name}</h4>
                      <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{deal.brand}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-success">${deal.discounted_price}</span>
                      <span className="text-sm text-muted-foreground line-through">${deal.original_price}</span>
                      <span className="px-2 py-0.5 rounded-full bg-success/20 text-success text-xs font-medium">
                        -{deal.discount_percent}%
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default TrackerDetail;
